import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { useTRPC } from "@/integrations/trpc/react";

export function useCheckIn({
  tournamentDivisionId,
  enabled,
}: {
  tournamentDivisionId: number;
  enabled: boolean;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const detailsQueryOptions = trpc.tournaments.getTeamDetails.queryOptions({
    tournamentDivisionId,
  });

  const { data: details } = useQuery({
    ...detailsQueryOptions,
    enabled,
  });

  const checkInMap = useMemo(
    () => new Map(details?.checkIns.map((c) => [c.playerProfileId, c]) ?? []),
    [details],
  );

  const { mutate: toggleCheckIn } = useMutation({
    mutationFn: trpc.tournaments.tdCheckIn.mutationOptions().mutationFn,
    onMutate: async (input) => {
      await queryClient.cancelQueries(detailsQueryOptions);
      const previous = queryClient.getQueryData(detailsQueryOptions.queryKey);

      queryClient.setQueryData(
        detailsQueryOptions.queryKey,
        (old: typeof details) => {
          if (!old) return old;
          const exists = old.checkIns.some(
            (c) => c.playerProfileId === input.playerProfileId,
          );
          return {
            ...old,
            checkIns: exists
              ? old.checkIns.filter(
                  (c) => c.playerProfileId !== input.playerProfileId,
                )
              : [
                  ...old.checkIns,
                  {
                    playerProfileId: input.playerProfileId,
                    method: "td" as const,
                    createdAt: new Date(),
                  },
                ],
          };
        },
      );

      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          detailsQueryOptions.queryKey,
          context.previous,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(detailsQueryOptions);
    },
  });

  const toggle = useCallback(
    (playerProfileId: number) =>
      toggleCheckIn({ tournamentDivisionId, playerProfileId }),
    [toggleCheckIn, tournamentDivisionId],
  );

  return {
    details,
    detailsQueryOptions,
    checkInMap,
    toggleCheckIn: toggle,
  };
}
